import { useEnvContext } from "context/EnvContext";
import { ARH_XSL_FILENAME, REHAB_FORM_NAMES } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getStaticAsset } from "core/model/config";
import {
  FileHISStateful,
  Filev2Stateful,
  RehabFormName,
  RehabForms,
} from "core/types";
import { getOntology } from "dsl/ecosystems/AcpTranslations/utils";
import { getRehabFormConfig } from "dsl/ecosystems/PatientForms/RehabForms/configs";
import { RehabFormDataMapped } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { getCachedTemplate } from "dsl/ecosystems/PatientForms/utils/pdfTemplateCache";
import { resolveArhVersion } from "onprem/resolveArhVersion";
import { createAHBRequestXml } from "onprem/utils";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { useCallback } from "react";
import { useTracking } from "react-tracking";
import { transformXmlToPdf } from "./useAHBResponseToPDF";

export type CreateRehaPdfProps = {
  formData: RehabFormDataMapped<RehabFormName>;
  formName: RehabFormName;
  forms: RehabForms | undefined;
  externalId: string;
  files?: (FileHISStateful | Filev2Stateful)[];
  documentNumber: number;
};

const formPageRanges: Partial<
  Record<RehabFormName, { endPage: number; startPage: number }>
> = {
  [REHAB_FORM_NAMES.GKV_GENERAL]: { endPage: 4, startPage: 1 },
  [REHAB_FORM_NAMES.GKV_MEDICAL]: { endPage: 10, startPage: 5 },
};

export const useCreateRehaPdf = () => {
  const { env } = useEnvContext();
  const { currentLocale: locale } = useDateLocale();
  const { trackEvent } = useTracking();

  const createRehaPdf = useCallback(
    async ({
      documentNumber,
      externalId,
      files,
      formData,
      formName,
      forms,
    }: CreateRehaPdfProps) => {
      const { pdfConfig } = getRehabFormConfig(formName);
      if (pdfConfig.pdfFiller) {
        const formUrl = getStaticAsset(pdfConfig.blankPdfAssetPath);
        const formPdfBytes = await getCachedTemplate(formUrl);
        const pdfDoc = await PDFDocument.load(formPdfBytes);
        const form = pdfDoc.getForm();

        pdfConfig.pdfFiller({ form, formData, locale });

        // burn values into PDF so we dont need to render annotation layers
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        form.updateFieldAppearances(font);

        form.flatten();

        const pdfBytes = await pdfDoc.save();
        return pdfBytes;
      } else {
        const { xml } = await createAHBRequestXml({
          documentNumber,
          env,
          externalId,
          files,
          formName,
          forms,
          trackEvent,
        });
        const version = resolveArhVersion(forms);
        const xslFilename = ARH_XSL_FILENAME[version];
        const xslUrl = getStaticAsset(xslFilename);
        const res = await fetch(xslUrl);
        if (!res.ok) throw new Error("Failed to load XSL");
        const xsl = await res.text();

        return transformXmlToPdf({
          xml,
          xsl,
          ...formPageRanges[formName],
        });
      }
    },
    [locale, getOntology],
  );

  return createRehaPdf;
};
